import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { checkSupportAccess, getTickets } from '../services/supportService';
import './SupportPortal.css';

const CATEGORY_LABELS = {
  billing: 'Billing',
  technical: 'Technical',
  account: 'Account',
  coaching: 'Coaching',
  nutrition: 'Nutrition',
  community_kitchen: 'Community Kitchen',
  general: 'General'
};

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting_on_customer: 'Awaiting Reply',
  resolved: 'Resolved',
  closed: 'Closed'
};

const PRIORITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };

const SupportPortal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAccess();
  }, []);

  useEffect(() => {
    if (hasAccess) {
      loadTickets();
    }
  }, [hasAccess, statusFilter, page]);

  const loadAccess = async () => {
    try {
      const data = await checkSupportAccess();
      setHasAccess(data.hasAccess);
    } catch (err) {
      console.error('Support access check failed:', err);
      setError(err.message);
      toast.error('Failed to check support access');
    } finally {
      setLoading(false);
    }
  };

  const loadTickets = async () => {
    try {
      const data = await getTickets({ status: statusFilter, page });
      setTickets(data.tickets);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (err) {
      toast.error('Failed to load tickets');
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="support-portal" style={{ padding: '3rem', textAlign: 'center' }}>
        <div className="support-loading">
          <div className="loading-spinner" />
          <p>Checking support access...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="support-portal">
        <div className="support-container">
          <div className="support-upgrade-wall">
            <div className="upgrade-icon">⚠️</div>
            <h2>Something went wrong</h2>
            <p>We couldn't load the support portal. Please try again later.</p>
            <button className="upgrade-btn" onClick={() => window.location.reload()}>Refresh Page</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="support-portal">
      <div className="support-container">
        <div className="support-header">
          <div className="support-header-text">
            <h1>Support Portal</h1>
            <p>Get help with your account, billing, or technical issues</p>
          </div>
          {hasAccess && (
            <button className="support-new-btn" onClick={() => navigate('/support/new')}>
              + New Ticket
            </button>
          )}
        </div>

        {!hasAccess ? (
          <div className="support-upgrade-wall">
            <div className="upgrade-icon">🔒</div>
            <h2>Support Portal Access</h2>
            <p>
              Support tickets are available on paid plans. Upgrade to Standard or Premium to submit
              tickets and get personalized help from our team.
            </p>
            <p className="upgrade-faq-note">
              In the meantime, check out our <Link to="/faq">FAQ</Link> for answers to common questions.
            </p>
            <Link to="/pricing" className="upgrade-btn">View Plans & Pricing</Link>
          </div>
        ) : (
          <>
            <div className="support-filters">
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="support-filter-select"
              >
                <option value="all">All Tickets</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="waiting_on_customer">Awaiting Reply</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <span className="support-count">{total} ticket{total !== 1 ? 's' : ''}</span>
            </div>

            {tickets.length === 0 ? (
              <div className="support-empty">
                <div className="empty-icon">📋</div>
                <h3>No tickets yet</h3>
                <p>When you submit a support ticket, it will appear here.</p>
                <button className="support-new-btn" onClick={() => navigate('/support/new')}>
                  Create Your First Ticket
                </button>
              </div>
            ) : (
              <div className="support-ticket-list">
                {tickets.map((ticket) => (
                  <Link
                    key={ticket._id}
                    to={`/support/ticket/${ticket._id}`}
                    className="support-ticket-card"
                  >
                    <div className="ticket-card-top">
                      <span className="ticket-number">{ticket.ticketNumber}</span>
                      <span className={`ticket-status status-${ticket.status}`}>
                        {STATUS_LABELS[ticket.status]}
                      </span>
                    </div>
                    <h3 className="ticket-subject">{ticket.subject}</h3>
                    <div className="ticket-card-meta">
                      <span className={`ticket-category cat-${ticket.category}`}>
                        {CATEGORY_LABELS[ticket.category]}
                      </span>
                      <span className={`ticket-priority priority-${ticket.priority}`}>
                        {PRIORITY_LABELS[ticket.priority]}
                      </span>
                      <span className="ticket-date">{formatDate(ticket.updatedAt)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="support-pagination">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Previous</button>
                <span>Page {page} of {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SupportPortal;
